// ── Crosshair Config Types ──
// Standalone config types and lightweight preview sizing.
// Both settings.ts and crosshair-settings.ts import from here.

import { isMobile } from '../core/device';

export interface CrosshairConfig {
  color: string;           // hex color, default '#00ff00'
  size: number;            // line length in px, default 11
  gap: number;             // center gap in px, default 4
  thickness: number;       // line thickness in px, default 2
  outline: boolean;        // black outline, default true
  outlineThickness: number; // outline px, default 1
  dot: boolean;            // center dot, default false
  dynamic: boolean;        // expand with spread/movement, default true
  alpha: number;           // 0-1 opacity, default 1.0
  style: 'classic' | 'dot' | 'circle'; // crosshair style, default 'classic'
}

export const DEFAULT_CROSSHAIR_CONFIG: CrosshairConfig = {
  color: '#00ff00',
  size: 11,
  gap: 4,
  thickness: 2,
  outline: true,
  outlineThickness: 1,
  dot: false,
  dynamic: true,
  alpha: 1.0,
  style: 'classic',
};

export const CROSSHAIR_PRESET_SLOT_COUNT = 5;
const SETTINGS_STORAGE_KEY = 'counter_pump_settings';
const PRESET_MOUNT_ATTR = 'data-crosshair-presets-mounted';

export function getCrosshairPreviewCanvasSize(): number {
  return isMobile() ? 120 : 200;
}

export function getCrosshairPresetKey(slot: number): string {
  return `crosshair-presets-${slot}`;
}

export function saveCrosshairPreset(slot: number, config: CrosshairConfig): boolean {
  if (!isValidPresetSlot(slot)) return false;
  try {
    localStorage.setItem(getCrosshairPresetKey(slot), JSON.stringify(config));
    return true;
  } catch {
    return false;
  }
}

export function loadCrosshairPreset(slot: number): CrosshairConfig | null {
  if (!isValidPresetSlot(slot)) return null;
  try {
    const raw = localStorage.getItem(getCrosshairPresetKey(slot));
    if (!raw) return null;
    return normalizeCrosshairConfig(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function mountCrosshairPresetControls(section: HTMLElement): void {
  if (section.getAttribute(PRESET_MOUNT_ATTR) === 'true') return;
  section.setAttribute(PRESET_MOUNT_ATTR, 'true');

  let selectedSlot = 1;
  const wrapper = document.createElement('div');
  wrapper.setAttribute('data-crosshair-presets', 'true');
  Object.assign(wrapper.style, {
    margin: '12px 0',
    padding: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.03)',
  });

  const label = document.createElement('div');
  Object.assign(label.style, {
    fontSize: '11px',
    color: '#777',
    marginBottom: '6px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  });
  label.textContent = 'Presets';
  wrapper.appendChild(label);

  const slotRow = document.createElement('div');
  Object.assign(slotRow.style, {
    display: 'flex',
    gap: '5px',
    marginBottom: '8px',
  });
  wrapper.appendChild(slotRow);

  const slotButtons: HTMLButtonElement[] = [];
  const updateSlotButtons = () => {
    for (const button of slotButtons) {
      const slot = Number(button.dataset.presetSlot);
      const active = slot === selectedSlot;
      button.style.background = active ? 'rgba(233,69,96,0.25)' : 'rgba(60,60,50,0.4)';
      button.style.borderColor = active ? '#e94560' : '#3a3a2a';
      button.style.color = active ? '#ffffff' : '#c8c090';
    }
  };

  for (let slot = 1; slot <= CROSSHAIR_PRESET_SLOT_COUNT; slot++) {
    const slotButton = document.createElement('button');
    slotButton.type = 'button';
    slotButton.dataset.presetSlot = String(slot);
    slotButton.textContent = String(slot);
    Object.assign(slotButton.style, {
      width: '28px',
      height: '24px',
      padding: '0',
      background: 'rgba(60,60,50,0.4)',
      border: '1px solid #3a3a2a',
      borderRadius: '3px',
      color: '#c8c090',
      cursor: 'pointer',
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      fontWeight: 'bold',
    });
    slotButton.addEventListener('click', () => {
      selectedSlot = slot;
      updateSlotButtons();
    });
    slotButtons.push(slotButton);
    slotRow.appendChild(slotButton);
  }

  const actionRow = document.createElement('div');
  Object.assign(actionRow.style, {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  });

  const status = document.createElement('div');
  Object.assign(status.style, {
    flexBasis: '100%',
    minHeight: '14px',
    fontSize: '10px',
    color: '#666',
    letterSpacing: '1px',
  });

  const saveButton = createPresetButton('SAVE PRESET');
  saveButton.addEventListener('click', () => {
    const current = readCrosshairConfigFromForm(section);
    const saved = saveCrosshairPreset(selectedSlot, current);
    status.textContent = saved ? `Saved slot ${selectedSlot}` : 'Could not save preset';
    status.style.color = saved ? '#4caf50' : '#e94560';
  });
  actionRow.appendChild(saveButton);

  const loadButton = createPresetButton('LOAD PRESET');
  loadButton.addEventListener('click', () => {
    const preset = loadCrosshairPreset(selectedSlot);
    if (!preset) {
      status.textContent = `Slot ${selectedSlot} is empty`;
      status.style.color = '#e8b849';
      return;
    }
    applyCrosshairConfigToForm(section, preset);
    status.textContent = `Loaded slot ${selectedSlot}`;
    status.style.color = '#4caf50';
  });
  actionRow.appendChild(loadButton);
  wrapper.appendChild(actionRow);
  wrapper.appendChild(status);

  const colorBlock = section.querySelector('#settings-ch-colors')?.parentElement;
  if (colorBlock?.parentElement) {
    colorBlock.parentElement.insertBefore(wrapper, colorBlock.nextSibling);
  } else {
    section.appendChild(wrapper);
  }
  updateSlotButtons();
}

function createPresetButton(text: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = text;
  Object.assign(button.style, {
    padding: '6px 10px',
    background: 'rgba(60,60,50,0.5)',
    border: '1px solid #3a3a2a',
    borderRadius: '3px',
    color: '#c8c090',
    cursor: 'pointer',
    fontFamily: '"Courier New", monospace',
    fontSize: '10px',
    letterSpacing: '1px',
  });
  return button;
}

function isValidPresetSlot(slot: number): boolean {
  return Number.isInteger(slot) && slot >= 1 && slot <= CROSSHAIR_PRESET_SLOT_COUNT;
}

function normalizeCrosshairConfig(raw: unknown): CrosshairConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Partial<CrosshairConfig>;
  const style = data.style === 'dot' || data.style === 'circle' ? data.style : 'classic';
  return {
    color: typeof data.color === 'string' ? data.color : DEFAULT_CROSSHAIR_CONFIG.color,
    size: toNumber(data.size, DEFAULT_CROSSHAIR_CONFIG.size),
    gap: toNumber(data.gap, DEFAULT_CROSSHAIR_CONFIG.gap),
    thickness: toNumber(data.thickness, DEFAULT_CROSSHAIR_CONFIG.thickness),
    outline: typeof data.outline === 'boolean' ? data.outline : DEFAULT_CROSSHAIR_CONFIG.outline,
    outlineThickness: toNumber(data.outlineThickness, DEFAULT_CROSSHAIR_CONFIG.outlineThickness),
    dot: typeof data.dot === 'boolean' ? data.dot : DEFAULT_CROSSHAIR_CONFIG.dot,
    dynamic: typeof data.dynamic === 'boolean' ? data.dynamic : DEFAULT_CROSSHAIR_CONFIG.dynamic,
    alpha: toNumber(data.alpha, DEFAULT_CROSSHAIR_CONFIG.alpha),
    style,
  };
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function readStoredCrosshairConfig(): CrosshairConfig {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CROSSHAIR_CONFIG };
    const parsed = JSON.parse(raw) as { crosshair?: Partial<CrosshairConfig> };
    return { ...DEFAULT_CROSSHAIR_CONFIG, ...(parsed.crosshair ?? {}) };
  } catch {
    return { ...DEFAULT_CROSSHAIR_CONFIG };
  }
}

function readCrosshairConfigFromForm(section: HTMLElement): CrosshairConfig {
  const stored = readStoredCrosshairConfig();
  const getInput = (id: string): HTMLInputElement | null =>
    section.querySelector<HTMLInputElement>(`#${id}`) ?? document.getElementById(id) as HTMLInputElement | null;
  const getNumber = (id: string, fallback: number): number => {
    const value = Number(getInput(id)?.value);
    return Number.isFinite(value) ? value : fallback;
  };
  const getChecked = (id: string, fallback: boolean): boolean => getInput(id)?.checked ?? fallback;
  const activeStyleButton = Array.from(section.querySelectorAll<HTMLElement>('.ch-style-btn'))
    .find((button) => {
      const style = `${button.style.border} ${button.style.borderColor}`.toLowerCase();
      return style.includes('#e94560') || style.includes('233, 69, 96');
    });
  const style = activeStyleButton?.dataset.style === 'dot' || activeStyleButton?.dataset.style === 'circle'
    ? activeStyleButton.dataset.style
    : stored.style;

  return {
    ...stored,
    color: getInput('settings-ch-color-custom')?.value ?? stored.color,
    size: getNumber('settings-ch-size-slider', stored.size),
    gap: getNumber('settings-ch-gap-slider', stored.gap),
    thickness: getNumber('settings-ch-thick-slider', stored.thickness),
    alpha: getNumber('settings-ch-alpha-slider', stored.alpha),
    outline: getChecked('settings-ch-outline', stored.outline),
    dot: getChecked('settings-ch-dot', stored.dot),
    dynamic: getChecked('settings-ch-dynamic', stored.dynamic),
    style,
  };
}

function applyCrosshairConfigToForm(section: HTMLElement, config: CrosshairConfig): void {
  const getInput = (id: string): HTMLInputElement | null =>
    section.querySelector<HTMLInputElement>(`#${id}`) ?? document.getElementById(id) as HTMLInputElement | null;
  const dispatch = (el: HTMLElement, type: 'input' | 'change'): void => {
    el.dispatchEvent(new Event(type, { bubbles: true }));
  };
  const setValue = (id: string, value: string): void => {
    const el = getInput(id);
    if (!el) return;
    el.value = value;
    dispatch(el, 'input');
  };
  const setChecked = (id: string, checked: boolean): void => {
    const el = getInput(id);
    if (!el) return;
    el.checked = checked;
    dispatch(el, 'change');
  };

  section.querySelector<HTMLElement>(`.ch-style-btn[data-style="${config.style}"]`)?.click();
  setValue('settings-ch-color-custom', config.color);
  setValue('settings-ch-size-slider', String(config.size));
  setValue('settings-ch-gap-slider', String(config.gap));
  setValue('settings-ch-thick-slider', String(config.thickness));
  setValue('settings-ch-alpha-slider', String(config.alpha));
  setChecked('settings-ch-outline', config.outline);
  setChecked('settings-ch-dot', config.dot);
  setChecked('settings-ch-dynamic', config.dynamic);
  persistCrosshairConfig(config);
  window.dispatchEvent(new CustomEvent<CrosshairConfig>('crosshair-preset-loaded', { detail: config }));
}

function persistCrosshairConfig(config: CrosshairConfig): void {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ ...parsed, crosshair: config }));
  } catch {
    // SettingsStore will still be updated through form events when available.
  }
}

function mountPresetControlsInDocument(): void {
  if (typeof document === 'undefined') return;
  if (typeof document.querySelectorAll !== 'function') return;
  const sections = document.querySelectorAll<HTMLElement>('[data-settings-section="crosshair"]');
  for (const section of sections) mountCrosshairPresetControls(section);
}

function startCrosshairPresetAutoMount(): void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  const start = () => {
    mountPresetControlsInDocument();
    if (!document.body) return;
    const observer = new MutationObserver(() => mountPresetControlsInDocument());
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading' && typeof document.addEventListener === 'function') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

startCrosshairPresetAutoMount();
